/**
 * Cost Sheet Utility Functions
 * All calculation and helper functions for cost sheet module
 */

// Interest rate constant
const INTEREST_RATE = 10; // Percentage per month

// Shipping cost constant (will be replaced with algorithm later)
const SHIPPING_COST_AED = 100;

/**
 * Calculate volume from dimensions
 * @param {number} length - Length in cm
 * @param {number} width - Width in cm
 * @param {number} height - Height in cm
 * @returns {number} Volume in cm³
 */
export const calculateVolume = (length, width, height) => {
  if (!length || !width || !height) return 0;
  return parseFloat(length) * parseFloat(width) * parseFloat(height);
};

/**
 * Calculate weight from volume (if needed)
 * Uses density approximation for general cargo
 * @param {number} volume - Volume in cm³
 * @returns {number} Weight in kg
 */
export const calculateWeightFromVolume = (volume) => {
  if (!volume || volume <= 0) return 0;
  // General cargo density: ~167 kg/m³, converting cm³ to m³
  const density = 167; // kg per m³
  const volumeInM3 = volume / 1000000; // Convert cm³ to m³
  return volumeInM3 * density;
};

/**
 * Get effective weight (greater of input weight or calculated weight)
 * @param {number} inputWeight - User input weight in kg
 * @param {number} calculatedWeight - Calculated weight from volume in kg
 * @returns {number} Effective weight in kg
 */
export const getEffectiveWeight = (inputWeight, calculatedWeight) => {
  const input = parseFloat(inputWeight) || 0;
  const calculated = parseFloat(calculatedWeight) || 0;
  return Math.max(input, calculated);
};

/**
 * Calculate interest for days between delivery and payment
 * @param {number} principal - Principal amount (input cost)
 * @param {number} days - Number of days between delivery and payment
 * @param {number} interestRate - Monthly interest rate percentage (defaults to constant, 10% per month)
 * @returns {number} Interest amount
 */
export const calculateInterest = (principal, days, interestRate = INTEREST_RATE) => {
  if (!principal || !days || days <= 0) return 0;
  const principalAmount = parseFloat(principal);
  const daysCount = parseInt(days);
  const rate = parseFloat(interestRate);
  
  // Monthly interest calculation: (Principal × Monthly Rate × Days) / (30 × 100)
  // 10% per month means for each 30 days, charge 10% of principal
  return (principalAmount * rate * daysCount) / (30 * 100);
};

/**
 * Fetch exchange rate from API
 * @param {string} fromCurrency - Source currency (USD/SAR)
 * @param {string} toCurrency - Target currency (AED)
 * @param {string} apiUrl - Exchange rate API URL
 * @returns {Promise<number>} Exchange rate
 */
export const fetchExchangeRate = async (fromCurrency, toCurrency, apiUrl) => {
  if (!apiUrl) {
    // Fallback rates if API not configured
    const fallbackRates = {
      'USD-AED': 3.67,
      'SAR-AED': 0.98
    };
    return fallbackRates[`${fromCurrency}-${toCurrency}`] || 1;
  }

  try {
    const response = await fetch(`${apiUrl}?from=${fromCurrency}&to=${toCurrency}`);
    if (!response.ok) {
      throw new Error('Failed to fetch exchange rate');
    }
    const data = await response.json();
    // Adjust based on your API response structure
    return data.rate || data.exchangeRate || 1;
  } catch (error) {
    console.error('Error fetching exchange rate:', error);
    // Return fallback rates
    const fallbackRates = {
      'USD-AED': 3.67,
      'SAR-AED': 0.98
    };
    return fallbackRates[`${fromCurrency}-${toCurrency}`] || 1;
  }
};

/**
 * Calculate final quote price
 * @param {Object} params - Calculation parameters
 * @param {number} params.inputCost - Base input cost
 * @param {number} params.shippingCost - Shipping cost in AED
 * @param {number} params.interestAmount - Interest amount
 * @param {number} params.margin - Margin percentage
 * @param {number} params.exchangeRate - Exchange rate to convert to base currency
 * @param {string} params.currency - Currency of input cost (USD/SAR)
 * @returns {number} Final quote price
 */
export const calculateFinalPrice = ({
  inputCost,
  shippingCost = SHIPPING_COST_AED,
  interestAmount,
  margin,
  exchangeRate = 1,
  currency = 'USD'
}) => {
  // Convert input cost to AED if needed
  let costInAED = parseFloat(inputCost) || 0;
  if (currency !== 'AED') {
    costInAED = costInAED * exchangeRate;
  }

  // Add shipping and interest
  const shippingInAED = parseFloat(shippingCost) || 0;
  const interestInAED = parseFloat(interestAmount) || 0;
  
  const totalCost = costInAED + shippingInAED + interestInAED;
  
  // Calculate final price with margin
  // Formula: Final Price = Total Cost / (1 - margin/100)
  const marginDecimal = parseFloat(margin) / 100;
  if (marginDecimal >= 1) {
    return totalCost; // Prevent division by zero or negative
  }
  
  const finalPrice = totalCost / (1 - marginDecimal);
  
  return Math.round(finalPrice * 100) / 100; // Round to 2 decimal places
};

/**
 * Calculate days between two dates
 * @param {Date} deliveryDate - Delivery date
 * @param {Date} paymentDate - Payment date
 * @returns {number} Number of days
 */
export const calculateDaysBetween = (deliveryDate, paymentDate) => {
  if (!deliveryDate || !paymentDate) return 0;
  
  const delivery = new Date(deliveryDate);
  const payment = new Date(paymentDate);
  
  if (isNaN(delivery.getTime()) || isNaN(payment.getTime())) return 0;
  
  const diffTime = payment.getTime() - delivery.getTime();
  const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
  
  return diffDays > 0 ? diffDays : 0;
};

/**
 * Get shipping cost based on mode
 * @param {string} mode - Shipping mode (air/ship)
 * @returns {number} Shipping cost in AED
 */
export const getShippingCost = (mode) => {
  // Fixed cost for now, will be replaced with algorithm later
  return SHIPPING_COST_AED;
};

// Export constants
export { INTEREST_RATE, SHIPPING_COST_AED };

